import axios from "axios";
import qs from "qs";
import { toast } from "sonner";

export interface ApiResponse {
  success: boolean;
  message: string;
  data: any;
  result?: any;
  page?: any;
  code?: number;
}
export interface ApiError {
  data?: {
    message?: string;
  };
  message?: string;
}
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim();

if (!apiBaseUrl) {
  throw new Error(
    "Missing VITE_API_BASE_URL. Configure it in the GitHub production environment before building.",
  );
}

const instance = axios.create({
  baseURL: apiBaseUrl.replace(/\/$/, ""),
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
  paramsSerializer: {
    serialize: (params) =>
      qs.stringify(params, {
        encode: true,
      }),
  },
});

// Improved request interceptor
instance.interceptors.request.use(
  (config) => {
    // Check both localStorage and sessionStorage on every request
    const accessToken = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const extractMessageFromPayload = (payload: any): string => {
  if (!payload) return '';
  if (typeof payload === 'string') return payload;
  if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message.trim();
  if (typeof payload?.error === 'string' && payload.error.trim()) return payload.error.trim();
  if (typeof payload?.detail === 'string' && payload.detail.trim()) return payload.detail.trim();
  if (typeof payload?.result?.message === 'string' && payload.result.message.trim()) return payload.result.message.trim();
  if (typeof payload?.data?.message === 'string' && payload.data.message.trim()) return payload.data.message.trim();
  if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
    return payload.errors
      .map((item: any) => item?.message || item?.defaultMessage || item)
      .filter(Boolean)
      .join('; ');
  }
  return '';
};

export const getApiErrorMessage = (error: unknown, fallback = 'Thao tác không thành công'): string => {
  const anyError = error as any;
  const payload = anyError?.response?.data ?? anyError?.data ?? anyError;
  return extractMessageFromPayload(payload) || anyError?.message || fallback;
};

// Enhanced response interceptor with better error handling
instance.interceptors.response.use(
  (response) => {
    const body = response.data;
    if (body && typeof body === "object") {
      const hasBackendCode = Object.prototype.hasOwnProperty.call(body, 'code');
      const hasSuccessFlag = Object.prototype.hasOwnProperty.call(body, 'success');
      const isBackendFailure =
        (hasBackendCode && body.code !== 1000) ||
        (hasSuccessFlag && body.success === false);

      if (isBackendFailure) {
        const message = extractMessageFromPayload(body) || `API trả về mã lỗi ${body.code ?? response.status}`;
        const apiError = new Error(message);
        (apiError as any).data = body;
        (apiError as any).status = response.status;
        toast.error(message);
        return Promise.reject(apiError);
      }

      if ("result" in body) {
        const result = body.result;
        const isPagedResponse =
          result &&
          typeof result === "object" &&
          !Array.isArray(result) &&
          Array.isArray(result.content);

        return {
          ...body,
          success: body.code === 1000 || body.success === true,
          data: isPagedResponse ? result.content : result,
          page: isPagedResponse ? result : undefined,
        };
      }
    }
    return body;
  },
  (error) => {
    // Handle 401 Unauthorized errors globally, but keep SIP/Asterisk 401 outside this axios instance unaffected.
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_token');
      window.location.href = '/login';
    }

    const message = getApiErrorMessage(error, 'Không thể kết nối đến máy chủ');
    if (error.response?.status !== 401) {
      toast.error(message);
    }

    const apiError = new Error(message);
    (apiError as any).data = error.response?.data ?? error.data;
    (apiError as any).status = error.response?.status ?? error.status;
    return Promise.reject(apiError);
  }
);

export default instance;
